import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import type { Transcript } from 'src/meetings/detail/tabs/transcript/types';

type RegeneratedSummary = {
  eventId: string;
  summary?: string;
  summaryStatus: Transcript['summaryStatus'];
};

/** Generation is a single LLM call the request waits on, well beyond the 25s default. */
const REGENERATE_TIMEOUT_MS = 60_000;

export const useRegenerateTranscriptSummary = (eventId: string) => {
  const queryClient = useQueryClient();

  const {
    mutateAsync,
    error,
    isPending: isRegenerating,
  } = useMutation<{ data: RegeneratedSummary }, Error, void>({
    mutationFn: () =>
      api.post<RegeneratedSummary>(`calendar-events/${eventId}/transcript-summary`, undefined, {
        timeoutMs: REGENERATE_TIMEOUT_MS,
      }),
    // Also on failure: the request timing out does not stop the server finishing, so refetching
    // picks up IN_PROGRESS and lets the transcript poll resolve it.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events', eventId, 'transcript'] });
    },
  });

  const regenerateSummary = async (): Promise<void> => {
    try {
      const { data } = await mutateAsync();
      // The attempt ran but produced nothing, which the endpoint reports as state rather than
      // a failed request, so it needs surfacing here or the click looks like it worked.
      if (data.summaryStatus === 'ERROR') {
        notifyAndLogError(
          'Could not regenerate the summary',
          'Please try again later.',
          new Error(`Summary regeneration returned ${data.summaryStatus}`),
          { eventId },
        );
      }
    } catch (mutationError) {
      notifyAndLogError(
        'Could not regenerate the summary',
        'Please try again later.',
        mutationError,
        { eventId },
      );
    }
  };

  return {
    regenerateSummary,
    isRegenerating,
    error,
  };
};
